<template>
  <section class="dashboard-kpis">
    <div class="kpi-header">
      <div>
        <span>Resumen diario</span>
        <h1>Panel de control</h1>
      </div>

      <div class="kpi-actions">
        <div
          v-if="kpis?.calculated_at"
          class="kpi-updated"
        >
          Actualizado: {{ formatDateTime(kpis.calculated_at) }}
        </div>

        <button
          class="kpi-refresh"
          type="button"
          :disabled="loading"
          @click="fetchKpis(true)"
        >
          <i
            class="mdi mdi-refresh"
            :class="{ 'mdi-spin': loading }"
          />
          Actualizar
        </button>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="kpi-error"
    >
      {{ errorMessage }}
    </div>

    <div class="kpi-grid">
      <article
        v-for="card in kpiCards"
        :key="card.key"
        class="kpi-card"
        :class="`kpi-card--${card.color}`"
      >
        <div class="kpi-content">
          <span>{{ card.title }}</span>

          <strong v-if="!loading">
            {{ formatNumber(card.value) }}
          </strong>

          <strong
            v-else
            class="kpi-skeleton"
          >
            ...
          </strong>

          <p>{{ card.subtitle }}</p>
        </div>

        <div class="kpi-icon">
          <i :class="card.icon" />
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const loading = ref(false)
const errorMessage = ref('')
const kpis = ref(null)

const kpiCards = computed(() => {
  const data = kpis.value || {}

  return [
    {
      key: 'active_campaigns_today',
      title: 'Campañas en curso',
      value: data.active_campaigns_today || 0,
      icon: 'mdi mdi-bullhorn-outline',
      color: 'primary',
      subtitle: 'Campañas activas de hoy',
    },
    {
      key: 'repartos_today',
      title: 'Repartos hoy',
      value: data.repartos_today || 0,
      icon: 'mdi mdi-truck-delivery-outline',
      color: 'info',
      subtitle: 'Zonas Traycco',
    },
    {
      key: 'flyers_today',
      title: 'Folletos hoy',
      value: data.flyers_today || 0,
      icon: 'mdi mdi-file-document-multiple-outline',
      color: 'success',
      subtitle: 'Volumen previsto',
    },
    {
      key: 'store_deliveries_pending',
      title: 'Entregas tienda',
      value: data.store_deliveries_pending || 0,
      icon: 'mdi mdi-store-outline',
      color: 'deep-purple',
      subtitle: 'Entregas para hoy',
    },
  ]
})

async function fetchKpis(force = false) {
  loading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/admin/dashboard/kpis', {
      params: {
        force: force ? 1 : 0,
      },
    })

    kpis.value = response.data
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se han podido cargar los KPIs del panel.'
  } finally {
    loading.value = false
  }
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('es-ES')
}

function formatDateTime(value) {
  if (!value) {
    return '-'
  }

  const text = String(value).replace('T', ' ')
  return text.slice(0, 16)
}

onMounted(() => {
  fetchKpis(false)
})
</script>

<style scoped>
.dashboard-kpis {
  margin-bottom: 22px;
  padding: 24px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 8px 26px rgba(15, 23, 42, 0.05);
}

.kpi-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.kpi-header span {
  display: block;
  margin-bottom: 6px;
  color: #a04b68;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-header h1 {
  margin: 0 0 6px;
  color: #102333;
  font-size: 1.45rem;
  font-weight: 900;
}

.kpi-header p {
  margin: 0;
  color: #6b7b88;
  font-size: 0.9rem;
}

.kpi-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.kpi-updated {
  color: #6b7b88;
  font-size: 0.78rem;
}

.kpi-refresh {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 0;
  border-radius: 12px;
  background: #762c45;
  color: #ffffff;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
}

.kpi-refresh:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(118, 44, 69, 0.22);
}

.kpi-refresh:disabled {
  opacity: 0.65;
  cursor: wait;
}

.kpi-error {
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 0.86rem;
  font-weight: 700;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.kpi-card {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-left: 6px solid transparent;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff, #fbfbfc);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
}

.kpi-content {
  min-width: 0;
}

.kpi-content span {
  display: block;
  margin-bottom: 8px;
  color: #6b7b88;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kpi-content strong {
  display: block;
  color: #102333;
  font-size: 2rem;
  font-weight: 900;
  line-height: 1;
}

.kpi-content p {
  margin: 8px 0 0;
  color: #6b7b88;
  font-size: 0.82rem;
}

.kpi-icon {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 15px;
  font-size: 26px;
}

.kpi-card--primary {
  border-left-color: #1976d2;
}

.kpi-card--primary .kpi-icon {
  color: #1976d2;
  background: #eaf2ff;
}

.kpi-card--info {
  border-left-color: #0288d1;
}

.kpi-card--info .kpi-icon {
  color: #0288d1;
  background: #e0f2fe;
}

.kpi-card--success {
  border-left-color: #2e7d32;
}

.kpi-card--success .kpi-icon {
  color: #2e7d32;
  background: #e7f8ef;
}

.kpi-card--deep-purple {
  border-left-color: #673ab7;
}

.kpi-card--deep-purple .kpi-icon {
  color: #673ab7;
  background: #f0e9ff;
}

.kpi-skeleton {
  color: #94a3b8 !important;
}

.mdi-spin {
  animation: mdi-spin 0.8s linear infinite;
}

@keyframes mdi-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 820px) {
  .kpi-header {
    flex-direction: column;
  }

  .kpi-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>